<template lang="pug">
form#service-form(name="service-form" @submit.prevent="submit" novalidate)
  .modal-card
    header.modal-card-head
      p.modal-card-title(v-once) {{ title }}
    section.modal-card-body
      b-field(
        label="Name"
        :message="errors.has('title') ? errors.first('title') : ''"
        :type="errors.has('title') ? 'is-danger' : ''"
      )
        b-input(
          type="text"
          name="title"
          v-validate="'required'"
          data-vv-as="name"
          :value="service.title"
          @input="set($event, 'title')"
        )
      b-field(grouped)
        b-field(label="Frequency")
          b-select(
            :value="service.frequency"
            @input="set($event, 'frequency')"
          )
            option(value="every") Every
            option(value="first") 1st
            option(value="second") 2nd
            option(value="third") 3rd
            option(value="fourth") 4th
            option(value="last") Last
            option(value="first_third") 1st &amp; 3rd
            option(value="second_fourth") 2nd &amp; 4th
        b-field(label="Day of Week" expanded)
          weekday-select(
            :value="service.weekday"
            @input="set($event, 'weekday')"
            expanded
          )
        b-field(
          label="Start Time"
          :message="errors.has('start_time') ? errors.first('start_time') : ''"
          :type="errors.has('start_time') ? 'is-danger' : ''"
        )
          b-timepicker(
            icon="clock"
            hour-format="12"
            name="start_time"
            ref="startTimepicker"
            v-validate="'required'"
            data-vv-as="start time"
            :increment-minutes="15"
            :value="service.start_time"
            @input="setTime($event, 'start_time')"
          )
            a.button.is-danger(@click="clearTime('start_time')")
              b-icon(icon="close")
              span Clear
        b-field(label="End Time")
          b-timepicker(
            icon="clock"
            hour-format="12"
            name="end_time"
            ref="endTimepicker"
            position="is-bottom-left"
            :increment-minutes="15"
            :value="service.end_time"
            @input="setTime($event, 'end_time')"
          )
            a.button.is-danger(@click="clearTime('end_time')")
              b-icon(icon="close")
              span Clear
      b-field(label="Channel")
        elasticsearch-multiselect(
          field="title"
          index="doxa_channels"
          :maxtags="1"
          :value="service.channel_id"
          @input="setId($event, 'channel_id')"
        )
      b-field(label="Ministry")
        elasticsearch-multiselect(
          field="name"
          index="doxa_ministries"
          :maxtags="1"
          :value="service.ministry_id"
          @input="setId($event, 'ministry_id')"
        )
      b-field(label="Age/Grade Range")
        b-input(
          type="text"
          :value="service.age_range"
          @input="set($event, 'age_range')"
        )
      .switches
        b-field
          b-switch(
            :value="service.is_live"
            @input="set($event, 'is_live')"
          ) Live stream
        b-field(v-if="service.ministry_id")
          b-switch(
            :value="service.is_ministry_only"
            @input="set($event, 'is_ministry_only')"
          ) Only show on ministry page
    footer.modal-card-foot
      button.button.is-success(
        type="submit"
        :class="{ 'is-loading': settings.submitting }"
      ) Save Service Time
      a.button.is-danger(v-if="!isNew" @click="confirmDelete") Delete
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import ElasticsearchMultiselect from '@/components/form/ElasticsearchMultiselect';
import WeekdaySelect from '@/components/form/WeekdaySelect';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'service-form',
  components: {
    ElasticsearchMultiselect,
    WeekdaySelect,
  },
  computed: {
    ...mapGetters({
      service: getters.GET_SERVICE,
    }),
    isNew() {
      return this.service.id === undefined;
    },
    title() {
      return this.isNew ? 'New Service Time' : 'Edit Service Time';
    },
  },
  data() {
    return {
      clear: {
        endTime: false,
        startTime: false,
      },
      settings: {
        loading: false,
        submitting: false,
      },
    };
  },
  methods: {
    clearTime(key) {
      const ref = key === 'end_time' ? 'endTimepicker' : 'startTimepicker';
      const clear = key === 'end_time' ? 'endTime' : 'startTime';
      this.clear[clear] = true;
      this.$refs[ref].close();
      this.set(null, key);
    },
    confirmDelete() {
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: 'Deleting this service cannot be undone',
        type: 'is-danger',
        hasIcon: true,
        confirmText: 'Yes, Delete Service',
        onConfirm: () => this.deleteService(),
      });
    },
    async deleteService() {
      const response = await this.$store.dispatch(actions.DO_DELETE_SERVICE, {
        id: this.service.id,
        ignore: true,
      });
      this.$parent.close();
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: response.message,
      });
      this.$store.commit(mutations.SET_MINISTRY_REFRESH, true);
    },
    set(val, key) {
      this.$store.commit(mutations.SET_SERVICE_FIELD, {
        key,
        val,
      });
    },
    setId(ids, key) {
      if (ids && ids.length > 0) {
        this.set(ids[0], key);
      } else {
        this.set(null, key);
      }
    },
    setTime(val, key) {
      const clear = key === 'end_time' ? 'endTime' : 'startTime';
      this.clear[clear] = false;
      this.set(val, key);
    },
    async submit() {
      const valid = await this.$validator.validateAll();
      if (valid) {
        this.settings.submitting = true;
        const action = this.isNew ? actions.DO_CREATE_SERVICE : actions.DO_UPDATE_SERVICE;
        const payload = {
          id: this.service.id,
          age_range: this.service.age_range,
          channel_id: this.service.channel_id,
          clear_end_time: this.clear.endTime,
          clear_start_time: this.clear.startTime,
          frequency: this.service.frequency,
          is_live: this.service.is_live,
          is_ministry_only: this.service.is_ministry_only,
          ministry_id: this.service.ministry_id,
          title: this.service.title,
          weekday: this.service.weekday,
        };
        const timestamps = [
          'end_time',
          'start_time',
        ];
        timestamps.forEach((timestamp) => {
          if (this.service[timestamp]) {
            const format = 'YYYY-MM-DD HH:mm Z';
            payload[timestamp] = moment(this.service[timestamp]).format(format);
          }
        });
        try {
          const response = await this.$store.dispatch(action, payload);
          this.$parent.close();
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-success',
            message: response.message,
          });
          this.settings.submitting = false;
          this.$store.commit(mutations.SET_MINISTRY_REFRESH, true);
        } catch (error) {
          this.settings.submitting = false;
          this.$store.commit(mutations.SET_APP_SNACKBAR, {
            type: 'is-danger',
            message: error.response.data.data.error_message,
          });
        }
      }
    },
  },
};
</script>

<style lang="scss">
#service-form {
  .switches {
    margin-top: 1.25rem;
  }
  .dropdown-content {
    &:focus {
      outline: none;
    }
    .dropdown-item {
      &:focus {
        outline: none;
      }
    }
  }
  .modal-card-body {
    min-height: 20rem;
    .is-grouped {
      margin-bottom: 0;
    }
    .timepicker {
      width: 126px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/form/ServiceForm.vue